import { useCallback, useContext, useMemo } from 'react'

import { ExperimentContext } from 'src/context/experiment'
import { useMarket } from '../useMarket'
import { useSession } from '../useSession'
import { reportExperiment, validateExperiment } from './helpers'
import { useDevice } from '../useDevice'
import { usePage } from '../usePage'
import { useBusiness } from '../useBusiness'

/**
 * ## useExperiment hook
 * @param experimentKey String key of the experiment. For example: 'WEB-0000'
 * @param validator Function to validate the experiment. If the function returns false, the experiment is not active
 * @returns
 *  - notify: Function to notify the experiment view. Receives the experiment key as parameter
 *  - variant: Number of the current variant. 0 if the experiment is not active (base)
 *
 * ### Usage
 * Notify the experiment view when the component is rendered
 * @example
 *  const { notify, variant } = useExperiment('WEB-0000');
 *  useEffectOnce(notify);
 *  if (variant === 1) {
 *    // Default behavior
 *  } else {
 *    // Variant behavior
 *  }
 *
 *  Notify the experiment view when a button is clicked
 *  @example
 *  const { notify, variant } = useExperiment('WEB-0000');
 *  useEffectOnce(notify);
 *  ...
 *  <Button onClick={notify}>Click me</Button>
 */
export const useExperiment = (
  experimentKey: string,
  validator: (variant: number) => boolean = () => true
) => {
  const { pageId } = usePage()
  const experiment = useContext(ExperimentContext)

  const { market, language, currency } = useMarket()
  const { isLogged, customer } = useSession()
  const { device, ip, isBot } = useDevice()
  const { affiliate } = useBusiness()

  const validatedExperiment = useMemo(
    () => validateExperiment(experiment, experimentKey),
    [experiment, experimentKey]
  )

  const notify = useCallback(() => {
    if (!validatedExperiment || !validator(validatedExperiment.variant)) {
      return
    }

    reportExperiment({
      affiliate,
      experiment,
      market,
      language,
      isLogged,
      customer,
      device,
      ip,
      isBot,
      pageId,
      currency,
    })
  }, [
    validatedExperiment,
    affiliate,
    experiment,
    market,
    language,
    isLogged,
    customer,
    device,
    ip,
    isBot,
    pageId,
    currency,
    validator,
  ])

  if (!validatedExperiment || !validator(validatedExperiment.variant)) {
    return { variant: 0, notify: () => null }
  }

  return { notify, variant: validatedExperiment.variant }
}

export default useExperiment
